import { FundWithDetailsEntity } from '@/src/lib/MayzSmartDB';
import { useTokensStore } from '@/src/store/global';
import { FundDataPrice } from '@/src/types/charts';
import { getNumberx1e6 } from '@/src/lib/MayzSmartDB';
import { formatDate } from '@/src/lib/SmartDB/Commons/formatters';
import { showADA_Amount_Rounded } from '@/src/store/global/tokensStoreModelHelpers';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts';
import styles from './ChartContainer.module.scss';
import { useMemo } from 'react';

interface ChartContainerProps {
    fund: FundWithDetailsEntity;
    isDesktop: boolean;
    isHide?: boolean;
    data: FundDataPrice[];
    isLoading?: boolean;
}

export default function ChartContainer({ fund, isDesktop, isHide = false, data, isLoading = false }: ChartContainerProps) {
    const tokensStore = useTokensStore();

    // Transform data based on display currency using useMemo
    const chartData = useMemo(() => {
        return data.map((item) => ({
            ...item,
            displayPrice: tokensStore.displayCurrency === 'usd' && item.priceUSDx1e6 !== undefined ? item.priceUSDx1e6 : item.priceADAx1e6,
        }));
    }, [data, tokensStore.displayCurrency]);

    if (isDesktop === false && isHide === true) return null;

    // Show loading state or empty chart while data is being fetched
    if (isLoading || data.length === 0) {
        return (
            <section className={styles.chartContainer}>
                <div className={`${styles.chart} ${styles.chartSkeleton}`}></div>
            </section>
        );
    }

    const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: { payload: FundDataPrice & { displayPrice: number } }[] }) => {
        if (active && payload && payload.length) {
            const dataPoint = payload[0].payload;
            const dateObj = new Date(dataPoint.date);
            const formattedDate = formatDate(dateObj, 'yyyy-MM-dd');

            const priceADAx1e6 = payload[0].payload.priceADAx1e6;
            // const priceUSDx1e6 = payload[0].payload.priceUSDx1e6;
            const adaUsdPriceForDate = payload[0].payload.adaUsdPriceForDate;

            const formattedADAPrice = `${showADA_Amount_Rounded(
                tokensStore,
                priceADAx1e6 !== undefined && fund.tokenFT!.decimals !== undefined ? getNumberx1e6(BigInt(priceADAx1e6) * BigInt(10 ** fund.tokenFT!.decimals)) : undefined,
                adaUsdPriceForDate
            )}`;

            return (
                <div className={styles.tooltipContainer}>
                    {/* <p className={styles.label}>{`${formattedDate}: ${formattedADAPrice}, ${priceADAx1e6} ADA - ${priceUSDx1e6} USD - ${adaUsdPriceForDate} RATE`}</p> */}
                    <p className={styles.label}>{`${formattedDate}: ${formattedADAPrice}`}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <section className={styles.chartContainer}>
            <div className={styles.chart}>
                <ResponsiveContainer width="100%" height={150}>
                    <AreaChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorChart" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="34.38%" stopColor="rgb(9, 191, 49)" stopOpacity={0.29} />
                                <stop offset="100%" stopColor="rgb(9, 191, 49)" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <Tooltip cursor={false} content={<CustomTooltip />} />
                        <Area type="bumpX" dataKey="displayPrice" stroke="#09BF31" fillOpacity={1} fill="url(#colorChart)" />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </section>
    );
}
